// This contains all the paths for the SEPT software APIs

// Runtime tenant configuration (P1, domain-model-transition)
export const pathApiConfig = "api/config";

// Organizations (P2, domain-model-transition — REST per code-standards/rest-conventions.md)
export const pathOrganizations = "api/organizations";
export const pathOrganizationId = (orgId: string) => `${pathOrganizations}/${orgId}`;
export const pathOrganizationMembers = (orgId: string) => `${pathOrganizations}/${orgId}/members`;
export const pathOrganizationMemberId = (orgId: string, clientId: string) =>
  `${pathOrganizations}/${orgId}/members/${clientId}`;
export const pathOrganizationMerge = (orgId: string) => `${pathOrganizations}/${orgId}/merge`;
export const pathOrganizationsByMember = (clientId: string) =>
  `${pathOrganizations}?member=${encodeURIComponent(clientId)}`;
export const pathOrganizationSummary = (orgId: string) => `${pathOrganizations}/${orgId}/summary`;
export const pathOrganizationMsaRates = (orgId: string) => `${pathOrganizations}/${orgId}/msa-rates`;
export const pathOrganizationMsaRateId = (orgId: string, rateId: string) =>
  `${pathOrganizations}/${orgId}/msa-rates/${rateId}`;

// Sites & jurisdictions (P6, domain-model-transition — REST per code-standards/rest-conventions.md)
// A site's matter is a QUERY PARAM, not a path segment: "nouns plural, filters via query params",
// so a matter's sites are /api/sites?matterid=X rather than /api/matters/X/sites.
export const pathSites = "api/sites";
export const pathSiteId = (siteId: string) => `${pathSites}/${siteId}`;
export const pathSitesByMatter = (matterId: string) =>
  `${pathSites}?matterid=${encodeURIComponent(matterId)}`;
export const pathSiteDeactivate = (siteId: string) => `${pathSites}/${siteId}/deactivate`;
export const pathSiteReactivate = (siteId: string) => `${pathSites}/${siteId}/reactivate`;
// Read-only by construction — jurisdictions are system-owned reference data with no tenant column,
// so there is deliberately no write path here to pair with the read (DMT-L2-JUR-SEC-01).
export const pathJurisdictions = "api/jurisdictions";

export const pathAgents = "agents";
export const pathAgentId = (agentId: string) => `${pathAgents}/${agentId}`;
export const pathAgentsSyncOutlook = "agents/sync-outlook";

export const pathAllMatters = "allmatters";
// P3: the RESTful matters resource — replaces the retired NCAL-named RPC family
// (kept server-side as deprecated aliases until P9).
export const pathApiMatters = "api/matters";
export const pathApiMatterId = (matterId: string): string => `api/matters/${matterId}`;
export const pathApiMatterTypes = "api/matter-types";


export const pathAssocCustomFields = "assoc-customfields";
export const pathAssocCustomFieldsByMatter = (matterId: string) => `${pathAssocCustomFields}/${matterId}/custom-fields`;

export const pathCheckUser = "check_user";
export const pathCheckUserByOid = (oid: string) => `${pathCheckUser}?cognitoSub=${oid}`;

// Backend endpoint paths
export const pathClientMatters = 'clientmatters';
export const pathClientMatterId = (clientId: string) => `${pathClientMatters}/${clientId}`;
export const pathClients = "clients";
export const pathClientId = (clientId: string) => `${pathClients}/${clientId}`;
export const pathClientCount = `${pathClients}/count`;
// P2 Build R: the frontend reads/writes through the REST alias (GET/PUT /clients/<id>);
// the legacy /getclient and /updateclient doors stay open on the backend, never removed.
export const pathClientGet = (clientId: string) => `${pathClients}/${clientId}`;
export const pathTasksByClient = (clientId: string) => `tasks_by_client/${clientId}`;
export const pathTasksCountByClient = (clientId: string) => `tasks_by_client/count/${clientId}`;

export const pathCreateEmployee = `create_employee`;
export const pathCreateFieldMember = `create_field_member`;
export const pathEnableSignin = (employeeId: string) => `employee/${employeeId}/enable_signin`;
export const pathCustomFields = "custom-fields";
export const pathCustomFieldId = (customFieldId: string) => `${pathCustomFields}/${customFieldId}`;

export const pathCustomValueHolding = "custom-value-holding";
export const pathCustomValueHoldingId = (vhId: string) => `custom-value-holding/${vhId}`

export const pathEmail = "email";
// Email sub-endpoints (under `email` base)
export const pathEmailContent = `${pathEmail}/content`;
export const pathEmailHolding = `${pathEmail}/holding`;
export const pathEmailMetadata = `${pathEmail}/metadata`;
export const pathEmailRecipients = `${pathEmail}/recipients`;
export const pathEmailUnified = `${pathEmail}/unified`;

export const pathEmployees = "employees";
export const pathEmployee = "employee";
export const pathEmployeeId = (employeeId: string) => `${pathEmployee}/${employeeId}`;
export const pathEmployeeIdPermish = (employeeId: string) => `${pathEmployeeId(employeeId)}/permish`;
export const pathEmployeeIdRates = (employeeId: string) => `${pathEmployeeId(employeeId)}/rates`;
export const pathGetEmployee = (employeeId: string) => `getemployee/${employeeId}`;

export const pathGroupRules = "group-rules";
export const pathGroupRuleId = (ruleId: string) => `${pathGroupRules}/${ruleId}`;

export const pathInvoiceFrom = 'invoice_from';
export const pathMatters = "matters";
export const pathMatterId = (matterId: string) => `${pathMatters}/${matterId}`;
export const pathMyMatters = "mymatters";
export const pathMyMattersEmployeeId = (employeeId: string) => `${pathMyMatters}/${employeeId}`;


// Additional matter endpoints (backend uses these routes)
export const pathMatterById = (matterId: string) => `matter_by_id/${matterId}`;
export const pathAddMatter = "addmatter";
export const pathMatterName = (matterId: string) => `matter_name/${matterId}`;
export const pathMatterStatusDistribution = "matter_status_distribution";

export const pathNotes = "notes";
export const pathNotesMatterId = (matterId: string) => `${pathNotes}/${matterId}`;
export const pathNotesMatterIdNoteId = (matterId: string, noteId: number) => `${pathNotes}/${matterId}/${noteId}`;

export const pathNotifications = "notifications";
export const pathNotificationId = (notificationId: string) => `${pathNotifications}/${notificationId}`;

export const pathOppExpenses = "opp_expenses";
export const pathOppExpensesMatterId = (matterId: string) => `${pathOppExpenses}/${matterId}`;
export const pathOppExpensesOpportunityId = (oppId: string) => `${pathOppExpenses}/${oppId}`;

export const pathReporting = "reporting";

export const pathRules = "rules";

export const pathUpdateClient = 'updateclient';  // legacy door, kept for callers not yet moved
// P2 Build R: updates go through the REST alias; response shape identical (same impl).
export const pathUpdateClientId = (clientId: string) => `${pathClients}/${clientId}`;
export const pathRenameClient = (clientId: string) => `rename_client/${clientId}`;

export const pathDeleteClient = (clientId: string) => `${pathClients}/${clientId}`;

export const pathTables = "tables";
export const pathTableId = (tableId: string) => `${pathTables}/${tableId}`;

export const pathWorkflows = "workflows";
export const pathWorkflowId = (workflowId: string) => `${pathWorkflows}/${workflowId}`;

// Workflow Template endpoints (PM Dev)
export const pathWorkflowTemplates = "pmdev/workflow/templates";
export const pathWorkflowTemplateId = (templateId: string) => `${pathWorkflowTemplates}/${templateId}`;
export const pathWorkflowTemplatesApply = `${pathWorkflowTemplates}/apply`;
export const pathWorkflowMilestoneId = (milestoneId: string) => `pmdev/workflow/milestones/${milestoneId}`;
export const pathWorkflowScheduleAggregates = "pmdev/workflow/schedules/aggregates";
export const pathWorkflowOverdueDetectionJob = "pmdev/workflow/jobs/overdue-detection";

// Milestone UI endpoints (Project Milestones Experience)
export const pathProjectMilestones = (projectId: string) => `projects/${projectId}/milestones`;
export const pathProjectMilestoneId = (projectId: string, milestoneId: string) => `projects/${projectId}/milestones/${milestoneId}`;

// Reporting Excel endpoints
export const pathReportingAllClientsExcel = 'allClients/excel';
export const pathReportingAllAgentsExcel = 'allAgents/excel';
export const pathReportingAllMattersExcel = 'allmatters/excel';
export const pathReportingAllTasksExcel = 'allTasks/excel';
export const pathReportingMyMattersExcel = (employeeId: string) => `mymatters/excel/${employeeId}`;
export const pathReportingMyTasksExcel = (employeeId: string) => `mytasks/excel/${employeeId}`;
export const pathReportingTasksByMatterExcel = (matterId: string) => `tasks_by_matter/excel/${matterId}`;


// Template Table (Engineering templates)
export const pathAddRowToTemplate = (tableName: string) => `add_row_to_template/${tableName}`;
export const pathDeleteRowFromTemplate = (tableName: string) => `delete_row_from_template/${tableName}`;
export const pathEditRowInTemplate = (tableName: string) => `edit_row_in_template/${tableName}`;
export const pathAllTemplates = 'all_templates';
export const pathChecklistTemplates = 'checklist_templates';
export const pathAddColumnToTemplate = (tableName: string) => `add_column_to_template/${tableName}`;
export const pathDeleteColumnFromTemplate = (tableName: string) => `delete_column_from_template/${tableName}`;
export const pathEditColumnNameInTemplate = (tableName: string) => `edit_column_name_in_template/${tableName}`;
export const pathTemplateTableData = (templateName: string) => `template_table_data/${templateName}`;
export const pathCustomTables = 'custom_tables';
export const pathCustomTablesHolding = 'custom_tables_holding';
export const pathCustomTableId = (tableId: number) => `${pathCustomTables}/${tableId}`;
export const pathCustomTableRows = (tableId: number) => `${pathCustomTableId(tableId)}/rows`;
export const pathCustomTableColumnsRows = (tableId: number) => `${pathCustomTableId(tableId)}/columns_rows`;
export const pathCustomTablesHoldingId = (holdingId: number) => `${pathCustomTablesHolding}/${holdingId}`;
export const pathCustomTablesHoldingRows = (holdingId: number) => `${pathCustomTablesHoldingId(holdingId)}/rows`;

export const pathAddTableToMatter = 'add_table_to_matter';
export const pathAvailableTablesForMatter = (matterId: string) => `available_tables_for_matter/${matterId}`;
export const pathDeleteCustomTable = (tableId: number) => `delete_custom_table/${tableId}`;
export const pathDeleteCustomTableHolding = (holdingId: number) => `delete_custom_table_holding/${holdingId}`;

export const pathTaskStatusDistribution = "task_status_distribution";
export const pathTaskStatusDistributionMatterId = (matterId: string) => `${pathTaskStatusDistribution}/${matterId}`;
export const pathMyTasks = "mytasks";
export const pathMyTasksEmployeeId = (employeeId: string) => `${pathMyTasks}/${employeeId}`;
export const pathAllTasks = 'alltasks';
export const pathAddTask = 'addtask';
export const pathTaskByMatters = 'tasks_by_matter';
export const pathTaskByMatterId = (matterId: string) => `${pathTaskByMatters}/${matterId}`;
export const pathTasksByMatterEmployee = 'tasks_by_matter_employee';
export const pathTasksByMatterEmployeeMatterId = (matterId: string) => `${pathTasksByMatterEmployee}/${matterId}`;
export const pathTasksByMatterEmployeeMatterIdEmployeeId = (matterId: string, employeeId: string) => `${pathTasksByMatterEmployeeMatterId(matterId)}/${employeeId}`;
export const pathTaskPending = 'task-pending';
export const pathTaskPendingTaskId = (taskId: string) => `${pathTaskPending}/${taskId}`;
export const pathTaskInProgress = 'task-inprogress';
export const pathTaskInProgressTaskId = (taskId: string) => `${pathTaskInProgress}/${taskId}`;
export const pathTaskComplete = 'task-complete';
export const pathTaskCompleteTaskId = (taskId: string) => `${pathTaskComplete}/${taskId}`;
export const pathUpdateTask = 'tasks';
export const pathUpdateTaskById = (taskId: string) =>
  `${pathUpdateTask}/${encodeURIComponent(taskId)}`;
export const pathTasksByMonth = 'tasks_by_month';
export const pathTasksByMonthMatterId = (matterId: string) => `${pathTasksByMonth}/${matterId}`;
export const pathTasksByMonthMatterIdMonth = (matterId: string, month: string) => `${pathTasksByMonthMatterId(matterId)}/${month}`;
export const pathRecentActivities = 'recent-activities';

export const pathVerifyTenant = "verify-tenant";
export const pathVenExpenses = "ven_expenses";
export const pathVenExpensesByMatter = (matterId: string) => `${pathVenExpenses}/${matterId}`;
export const pathVenExpenseById = (venId: string) => `${pathVenExpenses}/${venId}`;

// Checklist endpoints
export const pathChecklistItems = "checklistitems";
export const pathChecklistByMatter = (matterId: string) => `${pathChecklistItems}/${matterId}`;
export const pathCountIncompleteChecklistItems = "count-incomplete-checklist-items";
export const pathAddChecklistItem = "addchecklistitems";
export const pathEditChecklistItem = (itemId: string) => `edit_checklistitem/${itemId}`;
export const pathStatusChecklistItem = (itemId: string) => `statuschecklistitems/${itemId}`;
export const pathChecklistByMonth = (matterId: string, month: string) => `checklistitems_by_month/${matterId}/${month}`;
export const pathAllChecklistItemsByMonth = (month: string) => `all_checklistitems_by_month/${month}`;

// Project / deliverables
export const pathProjectDesignData = (matterId: string, tableName: string) => `project_design_data/${matterId}/${tableName}`;
export const pathProjectTableData = (matterId: string) => `project_table_data/${matterId}`;
export const pathProjectTableDataById = (pdId: string) => `project_table_data/${pdId}`;
export const pathProjectDesignDataById = (pdId: string) => `project_design_data/${pdId}`;

export const pathDeliverablesByMatter = (matterId: string) => `deliverables/${matterId}`;
export const pathDeliverableById = (dpId: string) => `deliverables/${dpId}`;

export const pathPermissions = "permissions";

// Engineering endpoints
export const pathEngClients = "eng_clients";
export const pathEngProjects = "eng-projects";
export const pathTemplateTables = "template-tables";

// Financial endpoints
export const pathFinancial = "financial";
export const pathInvoices = "invoice";
export const pathInvoiceId = (invoiceId: string) => `${pathInvoices}/${invoiceId}`;
export const pathUninvoicedSummary = 'uninvoiced_summary';
export const pathUninvoicedItems = (matterId: string) => `uninvoiced_items/${encodeURIComponent(matterId)}`;
export const pathInvoiceByMatter = 'invoice_by_matter'
export const pathInvoicesByMatterId = (matterId: string) => `${pathInvoiceByMatter}/${matterId}`;
export const pathFixedBills = "fixedbills";

// Financial endpoints used by FinancialService
export const pathAssignedFixedFees = "assigned_fixed_fees";
export const pathAssignedFixedFeesByClient = 'assigned_fixed_fees_by_client';
export const pathAssignedFixedFeesByMatter = (matterId: string) => `${pathAssignedFixedFees}/${matterId}`;
export const pathAssignedFixedFeesByClientId = (clientId: string) => `${pathAssignedFixedFeesByClient}/${clientId}`;
export const pathBilledTimeByClient = 'billed_time_by_client';
export const pathBilledTimeByClientId = (clientId: string) => `${pathBilledTimeByClient}/${clientId}`;
export const pathFixedFees = "fixed_fees";
export const pathAddFixedFees = "add_fixed_fees";
export const pathEditFixedFees = (feeCode: string) => `edit_fixed_fees/${feeCode}`;
export const pathDeleteFixedFees = (feeCode: string) => `delete_fixed_fees/${feeCode}`;

export const pathBilledTime = "billed_time";
export const pathLogTime = "log_time";
export const pathGetBillledTime = "get_billed_time";
export const pathGetBilledTimeByMatter = (matterId: string) => `${pathGetBillledTime}/${matterId}`;
export const pathGetAllBilledTime = "get_all_billed_time";
export const pathGetBilledTimeByMonth = (matterId: string, month: string) => `get_billed_time_by_month/${matterId}/${month}`;
export const pathGetAllBilledTimeByMonth = (month: string) => `get_all_billed_time_by_month/${month}`;
export const pathEditBilledTime = (btId: string) => `edit_billed_time/${btId}`;
export const pathDeleteBilledTime = (btId: string) => `${pathBilledTime}/${btId}`;
export const pathDeleteAssignedFixedFee = (assignedId: string) => `${pathAssignedFixedFees}/${assignedId}`;
export const pathCostOfWorkTime = "costofwork_time";
export const pathCostOfWorkTimeByMatter = (matterId: string) => `${pathCostOfWorkTime}/${matterId}`;
export const pathCostOfWorkTimeByEmployee = (employeeId: string) => `${pathCostOfWorkTime}/employee/${employeeId}`;
export const pathFieldRateTime = "fieldrate_time";
export const pathFieldRateTimeByMatter = (matterId: string) => `${pathFieldRateTime}/${matterId}`;
export const pathFieldRateTimeByEmployee = (employeeId: string) => `${pathFieldRateTime}/employee/${employeeId}`;

export const pathAgentDisbursements = "agent_disbursements";
export const pathAgentDisbursementsByMatter = (matterId: string) => `${pathAgentDisbursements}/${matterId}`;

export const pathAllAssignedFinancialData = "all_assigned_financial_data";
export const pathBillableTargets = (employeeId: string) => `billabletargets/${employeeId}`;
export const pathMyBilledTime = (employeeId: string) => `mybilledtime/${employeeId}`;

export const pathOtherBilling = "other_billing";
export const pathOtherBillingByMatter = (matterId: string) => `${pathOtherBilling}/${matterId}`;
export const pathOtherBillingById = (id: string) => `${pathOtherBilling}/${id}`;

export const pathFixedDisbursements = "fixed_disbursements";
export const pathFixedDisbursementById = (id: string) => `${pathFixedDisbursements}/${id}`;

// Fix bill expenses
export const pathFixBillExpenses = "fix_bill_expenses";
export const pathFixBillExpensesByMatter = (matterId: string) => `${pathFixBillExpenses}/${matterId}`;
export const pathFixBillExpenseById = (id: string) => `${pathFixBillExpenses}/${id}`;

// General expenses
// alias for the API endpoint used by services (some endpoints use '_' naming)
export const pathGenExpenses = "gen_expenses";
export const pathGenExpensesByMatter = (matterId: string) => `${pathGenExpenses}/${matterId}`;
export const pathGenExpenseById = (id: string) => `${pathGenExpenses}/${id}`;

// Matter-specific paths
export const pathEditMatter = (matterId: string) => `edit_matter/${matterId}`;
export const pathRenameMatter = (matterId: string) => `rename_matter/${matterId}`;
export const pathDeleteMatter = (matterId: string) => `delete/${matterId}`;
export const pathUpdateAssociatedEmployees = (matterId: string) => `update_associated_employees/${matterId}`;
export const pathUpdateMatterNote = (matterId: string, noteId: string) => `notes/${matterId}/${noteId}`;
export const pathDeleteMatterNote = (matterId: string, noteId: string) => `notes/${matterId}/${noteId}`;
export const pathMatterStatus = (matterId: string) => `matter-status/${matterId}`;
export const pathMatterWorkflow = (matterId: string) => `matter-workflow/${matterId}`;
export const pathMatterData = 'matter_data';
export const pathMatterDataMatterId = (matterId: string) => `${pathMatterData}/${matterId}`;

// Invoice Specific
export const pathCreatePaymentLink = "create-payment-link";

// Chart of Accounts endpoints
export const pathChartOfAccounts = "api/chart-of-accounts";
export const pathChartOfAccountsId = (accountId: string) => `${pathChartOfAccounts}/${accountId}`;
export const pathChartOfAccountsHierarchy = `${pathChartOfAccounts}/hierarchy`;
export const pathChartOfAccountsChildren = (accountId: string) => `${pathChartOfAccountsId(accountId)}/children`;
export const pathChartOfAccountsAvailableParents = `${pathChartOfAccounts}/available-parents`;
export const pathChartOfAccountsParent = (accountId: string) => `${pathChartOfAccountsId(accountId)}/parent`;
export const pathChartOfAccountsSearch = `${pathChartOfAccounts}/search`;
export const pathChartOfAccountsValidate = `${pathChartOfAccounts}/validate`;
export const pathChartOfAccountsDetails = (accountId: string) => `${pathChartOfAccountsId(accountId)}/details`;
export const pathChartOfAccountsTrustFlag = (accountId: string) => `${pathChartOfAccountsId(accountId)}/trust-flag`;
export const pathChartOfAccountsUsageStatistics = (accountId: string) => `${pathChartOfAccountsId(accountId)}/usage-statistics`;
export const pathChartOfAccountsImportTemplate = `${pathChartOfAccounts}/import-template`;
export const pathChartOfAccountsImportPreview = `${pathChartOfAccounts}/import-preview`;
export const pathChartOfAccountsImport = `${pathChartOfAccounts}/import`;
export const pathChartOfAccountsExport = `${pathChartOfAccounts}/export`;

// Account Number Structure endpoints
export const pathAccountNumberStructure = "api/account-number-structure";
export const pathAccountNumberStructureClassification = (classification: string) => `${pathAccountNumberStructure}/${classification}`;
export const pathAccountNumberStructureValidate = `${pathAccountNumberStructure}/validate`;
export const pathAccountNumberStructureSuggest = `${pathAccountNumberStructure}/suggest`;

// Journal endpoints
export const pathJournal = "api/journal";
export const pathJournalNew = `${pathJournal}/new`;
export const pathJournalId = (journalId: string) => `${pathJournal}/${journalId}`;
export const pathJournalBalance = (journalId: string) => `${pathJournalId(journalId)}/balance`;
export const pathJournalAutoSave = (journalId: string) => `${pathJournalId(journalId)}/auto-save`;
export const pathJournalValidateBalance = (journalId: string) => `${pathJournalId(journalId)}/validate-balance`;
export const pathJournalValidateAccount = `${pathJournal}/validate-account`;
export const pathJournalValidateAccounts = (journalId: string) => `${pathJournalId(journalId)}/validate-accounts`;
export const pathJournalPost = (journalId: string) => `${pathJournalId(journalId)}/post`;
export const pathJournalSearch = `${pathJournal}/search`;
export const pathJournalVoid = (journalId: string) => `${pathJournalId(journalId)}/void`;
export const pathJournalPeriodStatus = `${pathJournal}/period-status`;
export const pathJournalOpenPeriods = `${pathJournal}/open-periods`;
export const pathJournalStatusHistory = (journalId: string) => `${pathJournalId(journalId)}/status-history`;
export const pathJournalExport = `${pathJournal}/export`;
export const pathJournalLineItems = (journalId: string) => `${pathJournalId(journalId)}/line-items`;
export const pathJournalLineItemId = (journalId: string, lineItemId: string) => `${pathJournalLineItems(journalId)}/${lineItemId}`;
export const pathJournalImportTemplate = `${pathJournal}/import/template`;
export const pathJournalImportValidate = `${pathJournal}/import/validate`;
export const pathJournalImport = `${pathJournal}/import`;

// Journal Template endpoints
export const pathJournalTemplates = `${pathJournal}/templates`;
export const pathJournalTemplateId = (templateId: string) => `${pathJournalTemplates}/${templateId}`;
export const pathJournalTemplateApply = (templateId: string) => `${pathJournalTemplateId(templateId)}/apply`;

// General Ledger endpoints
export const pathGeneralLedger = "api/general-ledger";
export const pathGeneralLedgerBalances = `${pathGeneralLedger}/balances`;
export const pathGeneralLedgerEntries = `${pathGeneralLedger}/entries`;
export const pathGeneralLedgerEntriesByDateRange = `${pathGeneralLedger}/entries-by-date-range`;
export const pathGeneralLedgerEntryId = (entryId: string) => `${pathGeneralLedgerEntries}/${entryId}`;
export const pathGeneralLedgerBalanceCalculate = `${pathGeneralLedgerBalances}/calculate`;
export const pathGeneralLedgerExport = `${pathGeneralLedger}/export`;
export const pathGeneralLedgerBalanceHistory = `${pathGeneralLedger}/balance-history`;

// Period Management endpoints
export const pathPeriods = "api/periods";
export const pathPeriodsBulk = `${pathPeriods}/bulk`;
export const pathPeriodsCurrent = `${pathPeriods}/current`;
export const pathPeriodId = (periodId: string) => `${pathPeriods}/${periodId}`;
export const pathPeriodStatus = (periodId: string) => `${pathPeriodId(periodId)}/status`;
export const pathPeriodClose = (periodId: string) => `${pathPeriodId(periodId)}/close`;
export const pathPeriodCloseSummary = (periodId: string) => `${pathPeriodId(periodId)}/close-summary`;
export const pathPeriodReopen = (periodId: string) => `${pathPeriodId(periodId)}/reopen`;
export const pathPeriodLock = (periodId: string) => `${pathPeriodId(periodId)}/lock`;
export const pathPeriodValidateClose = (periodId: string) => `${pathPeriodId(periodId)}/validate-close`;
export const pathPeriodHistory = (periodId: string) => `${pathPeriodId(periodId)}/history`;
export const pathPeriodsHistory = `${pathPeriods}/history`;

// Period Type Configuration endpoints
export const pathPeriodTypesConfig = "api/period-types/config";
export const pathPeriodTypeConfig = (periodType: string) => `${pathPeriodTypesConfig}/${periodType}`;

// Company Rates endpoints
export const pathRates = "rates";
export const pathRateId = (rateId: string) => `${pathRates}/${rateId}`;

// Billing Categories endpoints (cost-code catalog — CRUD added in Phase 2)
export const pathBillingCategories = "billing_categories";
export const pathBillingCategoryId = (categoryId: string) => `${pathBillingCategories}/${categoryId}`;
export const pathBillingCategoryActive = (categoryId: string) => `${pathBillingCategories}/${categoryId}/active`;

// Per-project per-cost-code budgets (matter_cost_code_budgets)
export const pathMatterCostCodeBudgets = (matterId: string) => `matter_cost_code_budgets/${matterId}`;

// Timesheet day locks + time requests (plans/todo/timesheet-day-locks.md). Not served through
// /api/config on purpose: locks have their own endpoint so the boot config stays out of the blast radius.
export const pathTimesheetDayLocks = "timesheet_day_locks";
export const pathTimesheetTimeRequests = "timesheet_time_requests";
export const pathTimesheetTimeRequestAction = (
  requestId: string,
  action: "approve" | "decline" | "cancel",
) => `${pathTimesheetTimeRequests}/${encodeURIComponent(requestId)}/${action}`;
// Who gets the new-time-request email (plans/todo/time-request-email-recipients.md). Admins only.
export const pathTimesheetTimeRequestRecipients = `${pathTimesheetTimeRequests}/recipients`;

// Per-user timesheet view preferences — sticky project filter / show-all / My-All view
// (plans/todo/timesheet-project-filter-persistence.md). Caller-scoped: no employee id in the path
// or body; the server resolves the caller from the token.
export const pathTimesheetViewPrefs = "timesheet_view_prefs";

// Timesheet date locks — one-off dates, annual recurrences and holiday presets
// (plans/todo/timesheet-date-locks.md). Read through pathTimesheetDayLocks (`lockedDates`).
export const pathTimesheetDateLocks = "timesheet_date_locks";
export const pathTimesheetDateLock = (lockId: string) => `${pathTimesheetDateLocks}/${encodeURIComponent(lockId)}`;
export const pathTimesheetDateLockPreset = `${pathTimesheetDateLocks}/preset`;
